import { Component, ojs, app, state, v, component } from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    eParse,
    inform,
    empty,
    monetary,
    formatDateForHumans,
} from "../../../helpers";
import SolidIcon from "../../Blocks/SolidIcon";
import InfiniteScroll from "../../Blocks/InfiniteScroll";
import Spinner from "../../Blocks/Spinner";
import { dom } from "../../../fotastart";
import { dc, api, STRINGS } from "../../../declarations";
import { Capacitor } from "@capacitor/core";
import { getFirebaseToken } from "../../../firebase";

const h = app("h");
const router = app("router");

const getWhatsAppLink = (phone) => {
    if (!phone) return "#";
    let cleaned = phone.replace(/[^\d+]/g, "");
    if (cleaned.startsWith("0")) {
        cleaned = "254" + cleaned.substring(1);
    } else if (cleaned.startsWith("+")) {
        cleaned = cleaned.substring(1);
    }
    return `https://wa.me/${cleaned}`;
};

export default class DeliveryRequests extends Component {
    companyId = state(null);
    loading = state(true);
    page = 1;
    lastPage = false;
    initialized = false;

    async init() {
        if (this.initialized) return;
        this.initialized = true;
        this.page = 1;
        this.lastPage = false;
        this.fetchRequests();
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (c) c.init();

        if (Capacitor.isNativePlatform()) {
            getFirebaseToken();
        }
    }

    async fetchRequests(marker) {
        let spinner = null;
        if (marker) {
            spinner = h.Spinner({
                class: "p-3",
                parent: marker,
                resetParent: true,
            });
        } else {
            this.loading.value = true;
        }

        const holder = dom.get("[hook='dr-requests-list']");

        if (holder?.children.length === 0) {
            this.lastPage = false;
            this.page = 1;
        }

        if (this.lastPage) {
            return;
        }

        try {
            let payload = {
                page: this.page,
                interval: 15,
            };

            const res = await api.get(
                `companies/${this.companyId.value}/delivery-requests`,
                payload,
                { silent: true },
            );

            if (!res && marker) {
                spinner?.remove();
                return;
            }

            const requestsList = res.data.deliveryRequests.data || [];

            if (
                empty(requestsList) &&
                res.data.deliveryRequests.current_page === 1
            ) {
                h.div(
                    {
                        class: "d-flex flex-column align-items-center justify-content-center py-5 mt-4 bg-light rounded-4 border w-100 text-center",
                        style: "min-height: 350px; border-style: dashed !important; border-width: 2px !important;",
                        parent: holder,
                        resetParent: true,
                    },
                    h.div(
                        {
                            class: "bg-white p-4 rounded-circle shadow-sm mb-4 d-flex align-items-center justify-content-center",
                            style: "width: 80px; height: 80px;",
                        },
                        h.i({ class: "bi bi-truck fs-1 text-primary" }),
                    ),
                    h.h4(
                        { class: "fw-bold text-dark mb-2" },
                        "No Delivery Requests Found",
                    ),
                    h.p(
                        {
                            class: "text-secondary mb-4 px-4",
                            style: "max-width: 400px;",
                        },
                        "You haven't requested any deliveries yet.",
                    ),
                );
            } else {
                if (res.data.deliveryRequests.current_page === 1 && holder) {
                    dom.empty(holder);
                }

                for (let req of requestsList) {
                    let id = `dr-${req.id}`;
                    if (!dom.get(`[delivery-card-id="${id}"]`, holder)) {
                        this.renderDeliveryCard(req, holder);
                    }
                }

                if (
                    this.page >= res.data.deliveryRequests.last_page &&
                    holder
                ) {
                    this.lastPage = true;
                } else {
                    h.button(
                        h.i({
                            class: "bi bi-arrow-down-circle me-2 fs-5",
                            style: "line-height: 0;",
                        }),
                        "Load More",
                        {
                            parent: marker,
                            resetParent: true,
                            class: "btn btn-outline-primary d-flex align-items-center justify-content-center mx-auto my-4 rounded-pill px-4 fw-semibold",
                            listeners: {
                                click: () => this.fetchRequests(marker),
                            },
                        },
                    );
                }
            }

            this.page = res.data.deliveryRequests.current_page + 1;
        } catch (e) {
            console.error(e);
            inform("Failed to load delivery requests", STRINGS.ERROR);
        } finally {
            this.loading.value = false;
            spinner?.remove();
        }
    }

    render(companyIdState) {
        if (companyIdState) {
            this.companyId.value = companyIdState.value;
        }

        const businessName = window.currentCompany?.name || "Business";
        pageInfo(`Delivery Requests - ${businessName}`);
        scrollToTop();

        return h.div(
            { class: "min-vh-100 py-5 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                h.div(
                    {
                        class: "mb-5 d-flex align-items-center justify-content-between",
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.button(
                            {
                                class: "btn btn-white border shadow-sm rounded-circle p-2",
                                listeners: {
                                    click: () => {
                                        router.to("home");
                                    },
                                },
                            },
                            h.SolidIcon("arrow-left"),
                        ),
                        h.h2({ class: "fw-bold mb-0" }, "Delivery Requests"),
                    ),
                ),

                v(this.loading, (l) => {
                    if (l.value) {
                        return h.div(
                            { class: "text-center py-5" },
                            h.Spinner(),
                        );
                    }

                    return h.InfiniteScroll({
                        child: h.div({
                            class: "row g-4 w-100 m-0",
                            hook: "dr-requests-list",
                        }),
                        callback: (entries, observer, marker) => {
                            if (entries[0]?.isIntersecting && !this.lastPage) {
                                this.fetchRequests(marker);
                            }
                        },
                    });
                }),
            ),
        );
    }

    renderDeliveryCard(req, parent) {
        const getStatusBadge = (status) => {
            let bg = "bg-secondary";
            if (status === "confirmed") bg = "bg-info text-white";
            else if (status === "assigned") bg = "bg-primary text-white";
            else if (status === "delivering") bg = "bg-warning text-dark";
            else if (status === "completed") bg = "bg-success text-white";
            return h.span(
                {
                    class: `badge rounded-pill ${bg} px-3 py-2 text-capitalize fw-bold`,
                },
                status,
            );
        };

        h.div(
            {
                class: "col-12 col-md-6 col-lg-4",
                delivery_card_id: `dr-${req.id}`,
                parent,
            },
            h.div(
                {
                    class: "card border-0 shadow-sm h-100 overflow-hidden",
                },
                h.div(
                    { class: "card-body p-4 d-flex flex-column" },

                    // Header: Request Code/ID & Status badge
                    h.div(
                        {
                            class: "d-flex justify-content-between align-items-start mb-3",
                        },
                        h.div(
                            {},
                            h.div(
                                {
                                    class: "text-muted small text-uppercase fw-bold mb-1",
                                    style: "letter-spacing: 0.5px; font-size: 0.75rem;",
                                },
                                "Request ID",
                            ),
                            h.h5(
                                { class: "fw-bold mb-0 text-dark" },
                                `#${req.id}`,
                            ),
                        ),
                        getStatusBadge(req.status),
                    ),

                    // Pickup branch details
                    h.div(
                        { class: "mb-3 pb-3 border-bottom" },
                        h.div(
                            { class: "d-flex align-items-start gap-2" },
                            h.i({
                                class: "bi bi-geo-alt-fill text-danger fs-5",
                            }),
                            h.div(
                                {},
                                h.div(
                                    { class: "small text-muted fw-semibold" },
                                    "Pickup Branch",
                                ),
                                h.div(
                                    { class: "fw-bold text-dark" },
                                    req.pickup_branch
                                        ? req.pickup_branch.name
                                        : "Custom Pickup",
                                ),
                                req.pickup_branch?.readable_address
                                    ? h.small(
                                          { class: "text-muted" },
                                          req.pickup_branch.readable_address,
                                      )
                                    : null,
                            ),
                        ),
                    ),

                    // Recipient details
                    h.div(
                        { class: "mb-3 pb-3 border-bottom" },
                        h.div(
                            { class: "d-flex align-items-start gap-2" },
                            h.i({
                                class: "bi bi-person-fill text-primary fs-5",
                            }),
                            h.div(
                                {},
                                h.div(
                                    { class: "small text-muted fw-semibold" },
                                    "Recipient",
                                ),
                                h.div(
                                    { class: "fw-bold text-dark" },
                                    req.recipient_name,
                                ),
                                h.div(
                                    { class: "small text-muted" },
                                    req.recipient_number,
                                ),
                            ),
                        ),
                    ),

                    // Deliverer info & Contact Buttons
                    h.div(
                        { class: "mb-3 flex-grow-1" },
                        h.div(
                            { class: "small text-muted fw-semibold mb-2" },
                            "Deliverer",
                        ),
                        req.deliverer
                            ? h.div(
                                  { class: "d-flex flex-column gap-2" },
                                  h.div(
                                      {
                                          class: "d-flex align-items-center gap-2",
                                      },
                                      h.i({
                                          class: "bi bi-bicycle text-success fs-5",
                                      }),
                                      h.div(
                                          { class: "fw-bold text-dark" },
                                          `${req.deliverer.first_name} ${
                                              req.deliverer.last_name || ""
                                          }`,
                                      ),
                                  ),
                                  h.div(
                                      { class: "d-flex gap-2 mt-1" },
                                      h.a(
                                          {
                                              href: `tel:${req.deliverer.phone}`,
                                              class: "btn btn-sm btn-outline-primary rounded-pill px-3 d-inline-flex align-items-center gap-1",
                                          },
                                          h.i({ class: "bi bi-telephone" }),
                                          "Call",
                                      ),
                                      h.a(
                                          {
                                              href: getWhatsAppLink(
                                                  req.deliverer.phone,
                                              ),
                                              target: "_blank",
                                              class: "btn btn-sm btn-outline-success rounded-pill px-3 d-inline-flex align-items-center gap-1",
                                          },
                                          h.i({ class: "bi bi-whatsapp" }),
                                          "WhatsApp",
                                      ),
                                  ),
                              )
                            : h.div(
                                  {
                                      class: "text-muted small d-flex align-items-center gap-2 py-1",
                                  },
                                  h.div({
                                      class: "spinner-border spinner-border-sm text-secondary",
                                      role: "status",
                                  }),
                                  h.span({}, "Finding Deliverer..."),
                              ),
                    ),

                    // Pricing & Timing
                    h.div(
                        {
                            class: "pt-3 border-top d-flex justify-content-between align-items-center",
                        },
                        h.div(
                            {},
                            h.div({ class: "small text-muted" }, "Est. Fee"),
                            h.div(
                                { class: "fw-bold text-success" },
                                monetary(
                                    req.estimated_delivery_fee,
                                    dc.userCurrency,
                                ),
                            ),
                            req.distance !== null && req.distance !== undefined
                                ? h.small(
                                      {
                                          class: "text-muted d-block mt-1",
                                          style: "font-size: 0.8rem;",
                                      },
                                      h.i({ class: "bi bi-info-circle me-1" }),
                                      `${(req.distance / 1000).toFixed(
                                          1,
                                      )} KM • ~${Math.ceil(
                                          (req.duration + 600) / 60,
                                      )} mins`,
                                  )
                                : null,
                        ),
                        req.delivery_due_at
                            ? h.div(
                                  { class: "text-end" },
                                  h.div(
                                      { class: "small text-muted" },
                                      "Due By",
                                  ),
                                  h.small(
                                      { class: "fw-semibold text-secondary" },
                                      formatDateForHumans(req.delivery_due_at),
                                  ),
                              )
                            : null,
                    ),
                ),
            ),
        );
    }
}

ojs(DeliveryRequests);
